import { authenticatedFetch } from "@lumi/data";
import { useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

interface Submitter {
  id: string;
  email: string;
  organization: string | null;
}

interface Reviewer {
  id: string;
  email: string;
}

interface SubmissionAsset {
  id: string;
  kind: string;
  rawFileName: string | null;
  rawFileSize: string | null; // Converted from BigInt to string for JSON serialization
  rawVideoUrl: string | null;
  encodingStatus: string;
  encodingError: string | null;
  encodingCost?: string | null; // Optional for compatibility with provider components
  hlsUrl: string | null;
  dashUrl: string | null;
  durationSec: number | null;
  resolution: string | null;
  thumbnailUrl: string | null;
  streamVideoId: string | null;
  uploadCompletedAt: string | null;
  createdAt: string;
  // R2 metadata fields
  uploadedBy?: string | null;
  contentType?: string | null;
}

interface HistoryEntry {
  id: string;
  action: string;
  notes: string | null;
  previousStatus: string | null;
  newStatus: string | null;
  createdAt: string;
  performer: {
    id: string;
    email: string;
  } | null;
}

export interface Submission {
  id: string;
  title: string;
  slug: string | null;
  synopsis: string | null;
  description: string | null;
  year: number | null;
  country: string | null;
  languages: string[];
  durationMinutes: number | null;
  rating: string | null;
  directorData: unknown;
  castData: unknown;
  crewData: unknown;
  genreData: unknown;
  posterUrl: string | null;
  backdropUrl: string | null;
  logoUrl: string | null;
  horizontalTileUrl: string | null;
  verticalTileUrl: string | null;
  tmdbId: number | null;
  status: string;
  submittedBy: string;
  reviewedBy: string | null;
  reviewNotes: string | null;
  adminNotes: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  submitter: Submitter;
  reviewer: Reviewer | null;
  assets?: SubmissionAsset[];
  history?: HistoryEntry[];
  featureSubtitles?: SubtitleMetadata[];
  trailerSubtitles?: SubtitleMetadata[];
}

interface SubtitleMetadata {
  language: string;
  label: string;
  r2Key: string;
  fileName: string;
  fileSize?: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UseSubmissionsOptions {
  status?: string;
  provider?: string;
  page?: number;
  limit?: number;
}

interface UseSubmissionsResult {
  submissions: Submission[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  refetch: () => void;
}

interface UseSubmissionResult {
  submission: Submission | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch list of submissions with filters
 */
export function useSubmissions(options: UseSubmissionsOptions = {}): UseSubmissionsResult {
  const { accessToken, refreshAuth } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const fetchSubmissions = async () => {
    if (!accessToken) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (options.status) params.append("status", options.status);
      if (options.provider) params.append("provider", options.provider);
      if (options.page) params.append("page", options.page.toString());
      if (options.limit) params.append("limit", options.limit.toString());

      const data = await authenticatedFetch<{ data: Submission[]; pagination: PaginationInfo }>(
        `${API_BASE}/v1/admin/submissions?${params.toString()}`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setSubmissions(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error("Failed to fetch submissions:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSubmissions();
  }, [accessToken, options.status, options.provider, options.page, options.limit]);

  return {
    submissions,
    loading,
    error,
    pagination,
    refetch: () => {
      void fetchSubmissions();
    },
  };
}

/**
 * Hook to fetch a single submission by ID
 */
export function useSubmission(id: string): UseSubmissionResult {
  const { accessToken, refreshAuth } = useAuth();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const fetchSubmission = async () => {
    if (!id || !accessToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await authenticatedFetch<{ data: Submission }>(
        `${API_BASE}/v1/admin/submissions/${id}`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setSubmission(data.data);
    } catch (err) {
      console.error("Failed to fetch submission:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch submission");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSubmission();
  }, [id, accessToken]);

  return {
    submission,
    loading,
    error,
    refetch: () => {
      void fetchSubmission();
    },
  };
}
